import React, { createContext, useContext, useState, ReactNode } from 'react';
import enContent from '../locales/en.json';

// ─── Types ───────────────────────────────────────────────────────────────────
export type LangCode = 'en';

interface Language {
  code: LangCode;
  name: string;
  flag: string;
}

interface LanguageContextType {
  lang: LangCode;
  setLang: (code: LangCode) => void;
  t: typeof enContent;
  availableLanguages: Language[];
}

// ─── Available Languages ─────────────────────────────────────────────────────
// Add more languages here when translations are ready.
// The language switcher in the Navbar will only appear when there are 2+ languages.
export const AVAILABLE_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  // { code: 'ta', name: 'தமிழ்', flag: '🇮🇳' },  // Uncomment when Tamil translation is ready
  // { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },  // Uncomment when Hindi translation is ready
];

// ─── Content Map ─────────────────────────────────────────────────────────────
const contentMap: Record<LangCode, typeof enContent> = {
  en: enContent,
};

// ─── Context ─────────────────────────────────────────────────────────────────
const LanguageContext = createContext<LanguageContextType | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────
export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLang] = useState<LangCode>('en');

  const value: LanguageContextType = {
    lang,
    setLang,
    t: contentMap[lang],
    availableLanguages: AVAILABLE_LANGUAGES,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────
export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
